import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { StaffFeesPendingReportService, StudentFeeTermDetail } from '../services/staff-fees-pending-report.service';
import { StaffSessionService } from '../services/staff-session.service';

export interface StaffFeesReportStudentHistoryNavState {
  studentName: string;
  className: string;
  classNumber?: string;
  classDivision?: string;
}

@Component({
  selector: 'app-staff-fees-report-student-history',
  templateUrl: './staff-fees-report-student-history.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-fees-report-student-history.component.css']
})
export class StaffFeesReportStudentHistoryComponent implements OnInit {
  staffId: number | string | null = null;
  currencySymbol = '';

  classId: string = '';
  studentId: string = '';
  className = '';
  classNumber = '';
  classDivision = '';
  studentName = '';

  activeTab: 'unpaid' | 'history' = 'unpaid';
  fetchingStudentData = true;

  totalAmount = 0;
  pendingAmount = 0;
  paidAmount = 0;
  feesPercentagePending = 0;
  feesPercentagePaid = 0;

  pendingFeeInfo: StudentFeeTermDetail[] = [];
  paidFeeInfo: StudentFeeTermDetail[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffFeesPendingReportService: StaffFeesPendingReportService,
    private staffSession: StaffSessionService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';

    this.classId = this.route.snapshot.paramMap.get('classId') || '';
    this.studentId = this.route.snapshot.paramMap.get('studentId') || '';
    const state = history.state as StaffFeesReportStudentHistoryNavState;
    this.studentName = state?.studentName || '';
    this.className = state?.className || '';
    this.classNumber = state?.classNumber || '';
    this.classDivision = state?.classDivision || '';

    this.loadStudentHistory();
  }

  private async loadStudentHistory() {
    if (!this.staffId || !this.classId || !this.studentId) { this.fetchingStudentData = false; return; }
    this.fetchingStudentData = true;
    try {
      const response = await firstValueFrom(
        this.staffFeesPendingReportService.getOverdueStudentHistory(this.staffId, this.classId, this.studentId)
      );
      if (response?.status && response.body) {
        this.totalAmount = response.body.totalAmount || 0;
        this.pendingAmount = response.body.pendingAmount || 0;
        this.paidAmount = response.body.paidAmount || 0;
        this.feesPercentagePending = response.body.percentagePendingAmount || 0;
        // Derived client-side from the pending % the API gave — the API
        // never sends a paid % directly.
        this.feesPercentagePaid = 100 - this.feesPercentagePending;

        const feesDetail = response.body.feesDetail || [];
        this.paidFeeInfo = feesDetail.filter(f => f.isPaid);
        this.pendingFeeInfo = feesDetail.filter(f => !f.isPaid);
      } else {
        this.resetToEmpty();
      }
    } catch {
      // No error toast — matches the mobile app, which falls through
      // silently to zeros/empty state on failure.
      this.resetToEmpty();
    } finally {
      this.fetchingStudentData = false;
    }
  }

  private resetToEmpty() {
    this.totalAmount = 0;
    this.pendingAmount = 0;
    this.paidAmount = 0;
    this.feesPercentagePending = 0;
    this.feesPercentagePaid = 0;
    this.pendingFeeInfo = [];
    this.paidFeeInfo = [];
  }

  setTab(tab: 'unpaid' | 'history') {
    if (this.activeTab === tab) { return; }
    this.activeTab = tab;
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  formatDate(value: string | undefined): string {
    if (!value) { return ''; }
    const d = moment(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : value;
  }

  goBack() {
    // router.navigate here is itself a forward navigation, not a browser
    // "back" — history.state from the original students-list visit isn't
    // restored automatically, so its classNumber/classDivision (carried
    // forward into this screen's own nav state in ngOnInit) must be
    // explicitly passed along again here, or the students-list screen
    // reloads with no class name at all.
    this.router.navigate(['/staff/fees-report/class', this.classId], {
      state: { classNumber: this.classNumber, classDivision: this.classDivision }
    });
  }
}
